import React, { useState, useContext, useEffect } from 'react'
import {
  Container,
  HeaderDiv,
  HeaderText,
  CloseDiv,
  CloseImg,
  ProgressBarDiv,
  ProgressBarCircle,
  ProgressBarLine,
  InputRowDiv,
  InputDiv,
  InputLabel,
  Input,
  ButtonsDiv,
  CancelButton,
  NextButtonDiv,
  NextButtonImage,
  NextButtonText,
  WarningText,
} from './style'
import StockContext, { Stock } from '../../../../contexts/StockContext'
import ModalContext from '../../../../contexts/ModalContext'
import UserContext from '../../../../contexts/UserContext'
import cross from '../../../../assets/addStockModal/cross.svg'
import arrowRight from '../../../../assets/addStockModal/pages/arrow-right.svg'
import {
  FieldsToHeaders,
  fieldsToHeaders,
} from '../../../../pages/ClientPanel/Stock/StockTable/utils'
import 'font-awesome/css/font-awesome.min.css'
import InfiniteSelect from '../../../InfiniteSelect'
import PantoneSelect, { PantoneValue } from '../../../PantoneSelect'
import SelectSupplierInResource from '../../../Modals/Resource/CreateResource/SelectSupplier'
import ApiService from '../../../../services/ApiService'
import { Info } from 'lucide-react'

const FirstCustomAddPage: React.FC = () => {
  const { stock, setStock, resetContext } = useContext(StockContext)
  const { setModalActive, setModalSection, itIsUpdate, stockMode, isVariation } =
    useContext(ModalContext)
  const { user } = useContext(UserContext)
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({})
  const [warningMessage, setWarningMessage] = useState('')
  const [selectedColorObject, setSelectedColorObject] = useState<{ id: any; name: string; pantone: PantoneValue | null } | null>(null)
  const [pendingColorPantone, setPendingColorPantone] = useState<PantoneValue | null>(null)
  const [pantoneError, setPantoneError] = useState(false)
  const [customStatuses, setCustomStatuses] = useState<{ id: string; name: string }[]>([])
  const [deletedStatuses, setDeletedStatuses] = useState<Set<string>>(new Set())

  const isFashion = stockMode === 'fashion'

  const requiredFields = isFashion ? ['materialId', 'color'] : ['materialId', 'formatId', 'familyId', 'density']
  const numberFields = ['density', 'runNumber']

  useEffect(() => {
    if (!isFashion) return

    const existingPantone = stock.pantoneCode
      ? { code: stock.pantoneCode, hex: stock.pantoneHex || '' }
      : null

    if (stock.color) {
      setSelectedColorObject({
        id: stock.itemColorId || stock.color,
        name: stock.color,
        pantone: existingPantone,
      })

      if (existingPantone) {
        setPendingColorPantone(existingPantone)
      }
    } else {
      setSelectedColorObject(null)
      setPendingColorPantone(null)
    }
  }, [isFashion, stock.color, stock.itemColorId, stock.pantoneCode, stock.pantoneHex])

  const getHeaderTitle = () => {
    if (isVariation) return isFashion ? 'Criar Variação de Tecido' : 'Criar Variação de Estoque'
    if (isFashion) return itIsUpdate ? 'Editar Tecido' : 'Adicionar Tecido'
    return itIsUpdate ? 'Editar Estoque' : 'Adicionar Estoque'
  }
  const headerTitle = getHeaderTitle()

  const fieldsArrayNormal: string[][] = [
    ['material', 'heatTreatment', 'format'],
    ['originCountry', 'finishing', 'supplier'],
    ['stockStreet', 'shelf', 'stockLocation'],
    ['density', 'receivedDate', 'heatNumber'],
    ['family', 'fabrication', 'description'],
    ['characteristic', 'stockStatus', 'carrierCode'],
  ]

  const fieldsArrayFashion: string[][] = [
    ['material', 'format', 'originCountry'],
    ['finishing', 'supplier', 'stockStreet'],
    ['shelf', 'stockLocation', 'receivedDate'],
    ['heatNumber', 'family', 'fabrication'],
    ['description', 'characteristic', 'color'],
    ['stockStatus', 'carrierCode'],
  ]

  const fieldsArray = isFashion ? fieldsArrayFashion : fieldsArrayNormal

  const areRequiredFieldsFilled = () => {
    return requiredFields.every(
      (field) => {
        if (field === 'materialId') return !!stock.materialId || !!stock.material
        if (field === 'formatId') return !!stock.formatId || !!stock.format
        if (field === 'familyId') return !!stock.familyId || !!stock.family
        return !!stock[field as keyof Stock]
      }
    )
  }

  useEffect(() => {
    const newErrors: { [key: string]: boolean } = {}
    requiredFields.forEach((field) => {
      if (field === 'materialId' && !stock.materialId && !stock.material) {
        newErrors['material'] = true
      } else if (field === 'formatId' && !stock.formatId && !stock.format) {
        newErrors['format'] = true
      } else if (field === 'familyId' && !stock.familyId && !stock.family) {
        newErrors['family'] = true
      } else if (field !== 'materialId' && field !== 'formatId' && field !== 'familyId' && !stock[field as keyof Stock]) {
        newErrors[field] = true
      }
    })
    setErrors(newErrors)
    setWarningMessage('')
  }, [stock, isFashion])

  const applyDateMask = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 12)
    if (digits.length <= 2) return digits
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
    if (digits.length <= 8) return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
    if (digits.length <= 10) return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)} ${digits.slice(8)}h`
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)} ${digits.slice(8, 10)}h${digits.slice(10)}min`
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    if (numberFields.includes(name) && isNaN(Number(value))) {
      setWarningMessage(name)
      return
    }
    setWarningMessage('')
    const maskedValue = name === 'receivedDate' ? applyDateMask(value) : value
    setStock({ ...stock, [name]: maskedValue })
  }

  const fetchDensity = async (materialName: string) => {
    try {
      const response = await ApiService.get(`/backend/material/density/${materialName}`)
      const { density } = response.data
      setStock((prevStock) => ({
        ...prevStock,
        density: density != null ? density.toString() : '',
      }))
    } catch (error) {
      console.error('Error fetching density:', error)
    }
  }

  const getSelectValue = (fieldId: number | undefined, fieldName: string | undefined) => {
    if (fieldId !== undefined && fieldId !== null) {
      return { id: fieldId, name: fieldName || '' }
    }
    if (fieldName) {
      return { id: fieldName, name: fieldName }
    }
    return null
  }

  const handleSelectChange = (option: any, fieldIdName: string, fieldName: string) => {
    setStock(prev => ({
      ...prev,
      [fieldIdName]: option ? option.id : undefined,
      [fieldName]: option ? option.name : '',
    }))

    if (fieldName === 'material' && option?.name) {
      fetchDensity(option.name)
    }
  }

  const nextPage = () => {
    const newErrors: { [key: string]: boolean } = {}
    requiredFields.forEach((field) => {
      if (field === 'materialId' && !stock.materialId && !stock.material) {
        newErrors['material'] = true
      } else if (field === 'formatId' && !stock.formatId && !stock.format) {
        newErrors['format'] = true
      } else if (field === 'familyId' && !stock.familyId && !stock.family) {
        newErrors['family'] = true
      } else if (field !== 'materialId' && field !== 'formatId' && field !== 'familyId' && !stock[field as keyof Stock]) {
        newErrors[field] = true
      }
    })

    if (isFashion && stock.color && !stock.pantoneCode) {
      setPantoneError(true)
      newErrors['pantone'] = true
    }
    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) return

    setModalSection('secondCustomAdd')
  }

  return (
    <Container>
      <HeaderDiv>
        <HeaderText>{headerTitle}</HeaderText>
        <CloseDiv
          onClick={() => {
            setModalActive(false)
            resetContext()
          }}
        >
          <CloseImg src={cross} alt='Close tab' />
        </CloseDiv>
      </HeaderDiv>
      <ProgressBarDiv>
        <ProgressBarCircle className='active' />
        <ProgressBarLine />
        <ProgressBarCircle />
        <ProgressBarLine />
        <ProgressBarCircle />
      </ProgressBarDiv>
      {fieldsArray.map((inputRow, rowIndex) => (
        <InputRowDiv key={rowIndex}>
          {inputRow.map((stockField: string, colIndex) => {
            const isCrudField = [
              'material',
              'format',
              'family',
              'stockStreet',
              'shelf',
              'stockLocation',
              'fabrication',
              'heatTreatment',
              'originCountry',
              'finishing',
              'heatNumber',
              'stockStatus',
            ].includes(stockField)

            return (
              <InputDiv key={colIndex}>
                {stockField === 'supplier' ? (
                  <>
                    <InputLabel>Fornecedores</InputLabel>
                    <div style={{ width: '15.5vw', marginTop: '0.8vh' }}>
                      <SelectSupplierInResource
                        hideLabel={true}
                        inputStyle={{
                          height: '5.3vh',
                          borderRadius: '2vw',
                          boxSizing: 'border-box'
                        }}
                        suppliers={stock.suppliers || []}
                        setSuppliers={(val: any) => {
                          setStock((prev) => {
                            const current = prev.suppliers || []
                            const next = typeof val === 'function' ? val(current) : val
                            return { ...prev, suppliers: next }
                          })
                        }}
                      />
                    </div>
                  </>
                ) : isFashion && stockField === 'color' ? (
                  <>
                    <InputLabel>
                      Cor
                      <Info size={15} style={{ marginLeft: 5 }} color={!stock.color ? 'red' : 'gray'} />
                    </InputLabel>
                    <div style={{ width: '15.5vw', marginTop: '0.8vh' }}>
                      <InfiniteSelect
                        inputStyle={{
                          height: '5.3vh',
                          borderRadius: '2vw',
                          boxSizing: 'border-box'
                        }}
                        mode="crud"
                        value={selectedColorObject}
                        onSelect={option => {
                          if (!option) {
                            setSelectedColorObject(null)
                            setPendingColorPantone(null)
                            setStock(prev => ({ ...prev, color: '', pantoneCode: '', pantoneHex: '' }))
                            setErrors(prev => ({ ...prev, color: requiredFields.includes('color') }))
                            return
                          }
                          const colorItem = { id: option.id, name: option.name, pantone: (option as any).pantone ?? null }
                          setSelectedColorObject(colorItem)
                          setStock(prev => ({
                            ...prev,
                            color: option.name,
                            itemColorId: option.id ?? prev.itemColorId,
                            pantoneCode: colorItem.pantone?.code || prev.pantoneCode,
                            pantoneHex: colorItem.pantone?.hex || prev.pantoneHex,
                          }))
                          setErrors(prev => ({ ...prev, color: false }))
                          if (colorItem.pantone?.code) {
                            setPendingColorPantone(colorItem.pantone)
                            setStock(prev => ({ ...prev, pantoneCode: colorItem.pantone!.code, pantoneHex: colorItem.pantone!.hex || '' }))
                          } else {
                            setPendingColorPantone(null)
                            setStock(prev => ({ ...prev, pantoneCode: '', pantoneHex: '' }))
                          }
                        }}
                        fetchFunction={async (limit, offset, search?) => {
                          const response = await ApiService.stockLookup.getColors()
                          const items = Array.isArray(response.data) ? response.data : []
                          const mapped = items.map((c: any) => {
                            if (typeof c === 'string') return { id: c, name: c, pantone: null }
                            return { id: c.id || c.name, name: c.name, pantone: c.pantone ?? null }
                          })
                          return search
                            ? mapped.filter((m: any) => m.name?.toLowerCase().includes(search.toLowerCase()))
                            : mapped
                        }}
                        onItemCreate={async (name: string) => {
                          const tempItem = { id: `tmp-${Date.now()}`, name: name.trim(), pantone: null }
                          return tempItem as any
                        }}
                        onItemDelete={async (colorId: any) => {
                          const isTemp = typeof colorId === 'string' && String(colorId).startsWith('tmp-')
                          if (!isTemp) {
                            try { await ApiService.stockLookup.deleteColor(colorId) } catch (e) { console.error(e) }
                          }
                        }}
                        placeholder="Buscar ou criar cor"
                        allowCreate={true}
                        allowDelete={true}
                        showCreateOnlyIfNotExists={true}
                        minCreateLength={2}
                      />
                    </div>
                    {selectedColorObject && !selectedColorObject.pantone?.code && (
                      <div style={{ marginTop: 8, padding: 10, backgroundColor: '#f0f7ff', borderRadius: 8, border: '1px solid #bfdbfe' }}>
                        <label style={{ fontSize: 11, color: pantoneError ? '#e53e3e' : '#374151', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                          Pantone para <strong>{selectedColorObject.name}</strong> <span style={{ color: '#e53e3e' }}>*</span>
                        </label>
                        <PantoneSelect
                          value={pendingColorPantone}
                          onSelect={(p) => {
                            if (p) {
                              setSelectedColorObject(prev => prev ? { ...prev, pantone: p } : prev)
                              setPendingColorPantone(p)
                              setPantoneError(false)
                              setStock(prev => ({ ...prev, pantoneCode: p.code, pantoneHex: p.hex || '' }))
                            } else {
                              setPendingColorPantone(null)
                              setStock(prev => ({ ...prev, pantoneCode: '', pantoneHex: '' }))
                            }
                          }}
                          placeholder="Buscar Pantone (mín. 3 letras)"
                        />
                      </div>
                    )}
                    {selectedColorObject?.pantone?.code && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: 12, color: '#555' }}>
                        {selectedColorObject.pantone.hex && (
                          <span style={{
                            display: 'inline-block', width: 12, height: 12, borderRadius: 3,
                            backgroundColor: selectedColorObject.pantone.hex.startsWith('#') ? selectedColorObject.pantone.hex : `#${selectedColorObject.pantone.hex}`,
                            border: '1px solid rgba(0,0,0,0.15)',
                          }} />
                        )}
                        <span style={{ fontSize: 11 }}>{selectedColorObject.pantone.name ? `${selectedColorObject.pantone.name} (${selectedColorObject.pantone.code})` : selectedColorObject.pantone.code}</span>
                      </div>
                    )}
                    {errors['color'] && <WarningText>O campo Cor é obrigatório!</WarningText>}
                    {pantoneError && <WarningText>Selecione um Pantone para a cor!</WarningText>}
                  </>
                ) : isCrudField ? (
                  <>
                    <InputLabel>
                      {fieldsToHeaders[stockField as keyof FieldsToHeaders] || (stockField === 'stockStatus' ? 'Status' : stockField)}
                      {(requiredFields.includes(`${stockField}Id`) || requiredFields.includes(stockField)) && (
                        <Info
                          size={15}
                          style={{ marginLeft: 5 }}
                          color={`${!stock[`${stockField}Id` as keyof Stock] && !stock[stockField as keyof Stock]
                            ? 'red'
                            : 'gray'
                            }`}
                        />
                      )}
                    </InputLabel>
                    <div style={{ width: '15.5vw', marginTop: '0.8vh' }}>
                      <InfiniteSelect
                        inputStyle={{
                          height: '5.3vh',
                          borderRadius: '2vw',
                          boxSizing: 'border-box'
                        }}
                        mode="crud"
                        value={getSelectValue(
                          stock[`${stockField}Id` as keyof Stock] as number | undefined,
                          stock[stockField as keyof Stock] as string | undefined
                        )}
                        onSelect={(option) => handleSelectChange(option, `${stockField}Id`, stockField)}
                        showPantoneSwatch={false}
                        fetchFunction={async (limit, offset, search?) => {
                          let response: any
                          if (stockField === 'material') {
                            response = await ApiService.stockLookup.getMaterials({ type: 'STOCK' })
                          } else if (stockField === 'format') {
                            response = await ApiService.stockLookup.getFormats()
                          } else if (stockField === 'family') {
                            response = await ApiService.stockLookup.getFamilies()
                          } else if (stockField === 'stockStreet') {
                            response = await ApiService.stockLookup.getStockStreets()
                          } else if (stockField === 'shelf') {
                            response = await ApiService.stockLookup.getShelfs()
                          } else if (stockField === 'stockLocation') {
                            response = await ApiService.stockLookup.getStockLocations()
                          } else if (stockField === 'fabrication') {
                            response = await ApiService.stockLookup.getFabrications()
                          } else if (stockField === 'heatTreatment') {
                            response = await ApiService.stockLookup.getHeatTreatments()
                          } else if (stockField === 'originCountry') {
                            response = await ApiService.stockLookup.getOriginCountries()
                          } else if (stockField === 'finishing') {
                            response = await ApiService.stockLookup.getFinishings()
                          } else if (stockField === 'heatNumber') {
                            response = await ApiService.stockLookup.getHeatNumbers()
                          } else if (stockField === 'stockStatus') {
                            response = await ApiService.stockLookup.getStockStatuses()
                          }

                          const items = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : []
                          const mapped = items
                            .map((item: any, idx: number) => {
                              if (typeof item === 'string') return { id: item, name: item }
                              const statusName = item.name || item.type || item.ncm || item.status || ''
                              return {
                                id: item.id || statusName || `temp-${idx}`,
                                name: statusName,
                              }
                            })
                            .filter((item: any) => item.name && item.name.toLowerCase() !== 'null' && item.name.toLowerCase() !== 'undefined')

                          return search
                            ? mapped.filter((item: any) => item.name?.toLowerCase().includes(search.toLowerCase()))
                            : mapped
                        }}
                        onItemCreate={async (name: string) => {
                          let response: any
                          if (stockField === 'material') {
                            console.log('[DEBUG] FirstCustomAddPage -> Creating material:', { name: name.trim(), type: 'STOCK' })
                            response = await ApiService.stockLookup.createMaterial({ name: name.trim(), type: 'STOCK' })
                          } else if (stockField === 'format') {
                            response = await ApiService.stockLookup.createFormat({ name: name.trim() })
                          } else if (stockField === 'family') {
                            response = await ApiService.stockLookup.createFamily({ name: name.trim() })
                          } else if (stockField === 'stockStreet') {
                            response = await ApiService.stockLookup.createStockStreet({ name: name.trim() })
                          } else if (stockField === 'shelf') {
                            response = await ApiService.stockLookup.createShelf({ name: name.trim() })
                          } else if (stockField === 'stockLocation') {
                            response = await ApiService.stockLookup.createStockLocation({ name: name.trim() })
                          } else if (stockField === 'fabrication') {
                            response = await ApiService.stockLookup.createFabrication({ name: name.trim() })
                          } else if (stockField === 'heatTreatment') {
                            response = await ApiService.stockLookup.createHeatTreatment({ name: name.trim() })
                          } else if (stockField === 'originCountry') {
                            response = await ApiService.stockLookup.createOriginCountry({ name: name.trim() })
                          } else if (stockField === 'finishing') {
                            response = await ApiService.stockLookup.createFinishing({ name: name.trim() })
                          } else if (stockField === 'heatNumber') {
                            response = await ApiService.stockLookup.createHeatNumber({ name: name.trim() })
                          } else if (stockField === 'stockStatus') {
                            response = await ApiService.stockLookup.createStockStatus({ name: name.trim() })
                          }
                          return { id: response.data.id, name: response.data.name || response.data.type || '' }
                        }}
                        onItemDelete={async (id: any) => {
                          if (stockField === 'material') {
                            await ApiService.stockLookup.deleteMaterial(id)
                          } else if (stockField === 'format') {
                            await ApiService.stockLookup.deleteFormat(id)
                          } else if (stockField === 'family') {
                            await ApiService.stockLookup.deleteFamily(id)
                          } else if (stockField === 'stockStreet') {
                            await ApiService.stockLookup.deleteStockStreet(id)
                          } else if (stockField === 'shelf') {
                            await ApiService.stockLookup.deleteShelf(id)
                          } else if (stockField === 'stockLocation') {
                            await ApiService.stockLookup.deleteStockLocation(id)
                          } else if (stockField === 'fabrication') {
                            await ApiService.stockLookup.deleteFabrication(id)
                          } else if (stockField === 'heatTreatment') {
                            await ApiService.stockLookup.deleteHeatTreatment(id)
                          } else if (stockField === 'originCountry') {
                            await ApiService.stockLookup.deleteOriginCountry(id)
                          } else if (stockField === 'finishing') {
                            await ApiService.stockLookup.deleteFinishing(id)
                          } else if (stockField === 'heatNumber') {
                            await ApiService.stockLookup.deleteHeatNumber(id)
                          } else if (stockField === 'stockStatus') {
                            await ApiService.stockLookup.deleteStockStatus(id)
                          }
                        }}
                        placeholder={`Buscar ou criar ${fieldsToHeaders[stockField as keyof FieldsToHeaders] || stockField}`}
                        allowCreate={true}
                        allowDelete={true}
                        showCreateOnlyIfNotExists={true}
                        minCreateLength={2}
                      />
                    </div>
                    {errors[stockField] && (
                      <WarningText>O campo {fieldsToHeaders[stockField as keyof FieldsToHeaders] || stockField} é obrigatório!</WarningText>
                    )}
                  </>
                ) : (
                  <>
                    <InputLabel htmlFor={stockField}>
                      {fieldsToHeaders[stockField as keyof FieldsToHeaders] || stockField}
                      {requiredFields.includes(stockField) && (
                        <Info
                          size={15}
                          style={{ marginLeft: 5 }}
                          color={`${!stock[stockField as keyof Stock]
                            ? 'red'
                            : 'gray'
                            }`}
                        />
                      )}

                    </InputLabel>
                    <Input
                      placeholder={stockField === 'receivedDate' ? 'DD/MM/AAAA 00h00min' : 'Digite aqui'}
                      name={stockField}
                      value={stock[stockField as keyof Stock] || ''}
                      onFocus={() => {
                        setErrors((prev) => ({ ...prev, [stockField]: false }))
                        if (stockField === 'color') setWarningMessage('')
                      }}
                      onBlur={() => {
                        setErrors((prev) => ({
                          ...prev,
                          [stockField]:
                            requiredFields.includes(stockField) &&
                            !stock[stockField as keyof Stock],
                        }))
                      }}
                      onChange={handleChange}
                      autoComplete='off'
                      style={{
                        border: errors[stockField] ? '1.5px solid red' : undefined,
                      }}
                    />
                    {warningMessage === stockField && (
                      <WarningText>O campo tem que ser um número!</WarningText>
                    )}
                    {stockField === 'color' && errors['color'] && (
                      <WarningText>
                        O campo Cor é obrigatório para criar uma variação!
                      </WarningText>
                    )}
                  </>
                )}
              </InputDiv>
            )
          })}
        </InputRowDiv>
      ))}

      <ButtonsDiv>
        <CancelButton onClick={() => setModalSection('addStockMenu')}>
          Cancelar
        </CancelButton>
        <NextButtonDiv
          onClick={nextPage}
          style={{
            pointerEvents: areRequiredFieldsFilled() ? 'auto' : 'none',
            opacity: areRequiredFieldsFilled() ? 1 : 0.5,
          }}
        >
          <NextButtonImage src={arrowRight} alt='Right arrow icon' />
          <NextButtonText>Próximo</NextButtonText>
        </NextButtonDiv>
      </ButtonsDiv>
    </Container>
  )
}

export default FirstCustomAddPage
